import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiCopy, FiLink, FiSave } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import '../pages/CRM.css';

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const EMPTY_AVAILABILITY = Object.fromEntries(DAYS.map((d) => [d.key, []]));

const SUGGESTED_WEEKDAY_AVAILABILITY = {
  mon: [{ start: '09:00', end: '17:00' }],
  tue: [{ start: '09:00', end: '17:00' }],
  wed: [{ start: '09:00', end: '17:00' }],
  thu: [{ start: '09:00', end: '17:00' }],
  fri: [{ start: '09:00', end: '17:00' }],
  sat: [],
  sun: [],
};

function parseAvailability(raw) {
  if (raw == null || raw === '') return { ...EMPTY_AVAILABILITY };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ...EMPTY_AVAILABILITY };
    }
    return { ...EMPTY_AVAILABILITY, ...parsed };
  } catch {
    return { ...EMPTY_AVAILABILITY };
  }
}

/**
 * Consultant booking page settings — shareable URL + weekly hours.
 */
export default function BookingPageSettings({ compact = false }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    slug: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    slot_minutes: 30,
    buffer_minutes: 0,
    booking_window_days: 14,
    is_active: true,
    availability: EMPTY_AVAILABILITY,
  });

  const { data, isLoading } = useQuery(
    ['booking-page-me'],
    () => api.get('/booking-pages/me').then((r) => r.data),
    { staleTime: 30_000 }
  );

  useEffect(() => {
    if (!data) return;
    setForm({
      title: data.title || '',
      description: data.description || '',
      slug: data.slug || '',
      timezone: data.timezone || form.timezone,
      slot_minutes: data.slot_minutes || 30,
      buffer_minutes: data.buffer_minutes || 0,
      booking_window_days: data.booking_window_days || 14,
      is_active: data.is_active !== 0 && data.is_active !== false,
      availability: parseAvailability(data.availability_json || data.availability),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const shareUrl = useMemo(() => {
    if (!form.slug) return '';
    return `${window.location.origin}/book/${encodeURIComponent(form.slug)}`;
  }, [form.slug]);

  const saveMutation = useMutation(
    () =>
      api.put('/booking-pages/me', {
        title: form.title,
        description: form.description,
        slug: form.slug,
        timezone: form.timezone,
        slot_minutes: Number(form.slot_minutes),
        buffer_minutes: Number(form.buffer_minutes),
        booking_window_days: Number(form.booking_window_days),
        is_active: form.is_active,
        availability_json: form.availability,
      }),
    {
      onSuccess: (res) => {
        setMessage('Booking page saved.');
        queryClient.setQueryData(['booking-page-me'], res.data);
        queryClient.invalidateQueries(['booking-page-me']);
      },
      onError: (e) => setMessage(e.response?.data?.error || e.message),
    }
  );

  const toggleDay = (dayKey) => {
    setForm((prev) => {
      const cur = prev.availability[dayKey] || [];
      const next = { ...prev.availability };
      next[dayKey] = cur.length ? [] : [{ start: '09:00', end: '17:00' }];
      return { ...prev, availability: next };
    });
  };

  const setWindow = (dayKey, field, value) => {
    setForm((prev) => {
      const windows = [...(prev.availability[dayKey] || [])];
      if (!windows[0]) windows[0] = { start: '09:00', end: '17:00' };
      windows[0] = { ...windows[0], [field]: value };
      return {
        ...prev,
        availability: { ...prev.availability, [dayKey]: windows },
      };
    });
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setMessage('Share link copied.');
    } catch {
      setMessage(shareUrl);
    }
  };

  if (isLoading && !data) {
    return <p className="crm-modal-hint">Loading booking page…</p>;
  }

  return (
    <div className={`booking-settings ${compact ? 'booking-settings--compact' : ''}`}>
      {!compact && (
        <div className="settings-section-header">
          <h2>
            <FiLink /> Booking calendar
          </h2>
          <p>
            Publish weekly availability. Candidates assigned to you can book in CRM; anyone with the
            share link can book publicly.
          </p>
        </div>
      )}

      {message && <p className="crm-modal-hint">{message}</p>}

      <div className="form-grid">
        {shareUrl && (
          <div className="crm-share-link-card">
            <FiLink aria-hidden />
            <code>{shareUrl}</code>
            <button type="button" className="btn btn-secondary btn-sm" onClick={copyLink}>
              <FiCopy /> Copy link
            </button>
          </div>
        )}

        <label className="form-field">
          <span>Title</span>
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Career coaching call"
          />
        </label>
        <label className="form-field">
          <span>Share slug</span>
          <input
            value={form.slug}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
              }))
            }
            placeholder="ada-lovelace"
          />
        </label>
        <label className="form-field">
          <span>Description</span>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="What candidates can expect on this call"
          />
        </label>

        <div className="crm-metrics-row">
          <label className="form-field">
            <span>Slot (minutes)</span>
            <input
              type="number"
              min={15}
              step={15}
              value={form.slot_minutes}
              onChange={(e) => setForm((f) => ({ ...f, slot_minutes: e.target.value }))}
            />
          </label>
          <label className="form-field">
            <span>Buffer (minutes)</span>
            <input
              type="number"
              min={0}
              step={5}
              value={form.buffer_minutes}
              onChange={(e) => setForm((f) => ({ ...f, buffer_minutes: e.target.value }))}
            />
          </label>
          <label className="form-field">
            <span>Bookable days ahead</span>
            <input
              type="number"
              min={1}
              max={90}
              value={form.booking_window_days}
              onChange={(e) => setForm((f) => ({ ...f, booking_window_days: e.target.value }))}
            />
          </label>
          <label className="form-field">
            <span>Timezone</span>
            <input
              value={form.timezone}
              onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
            />
          </label>
        </div>

        <section className="crm-modal-section" aria-label="Weekly hours">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <p className="crm-modal-section-title">Weekly hours</p>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  availability: { ...EMPTY_AVAILABILITY, ...SUGGESTED_WEEKDAY_AVAILABILITY },
                }))
              }
            >
              Fill Mon–Fri 9–17
            </button>
          </div>
          <p className="crm-modal-hint">
            Candidates see open slots only after you enable days and save.
          </p>
          <div style={{ display: 'grid', gap: 6 }}>
            {DAYS.map((d) => {
              const windows = form.availability[d.key] || [];
              const enabled = windows.length > 0;
              const w = windows[0] || { start: '09:00', end: '17:00' };
              return (
                <div key={d.key} className={`crm-day-row ${enabled ? 'is-on' : ''}`}>
                  <label className="crm-day-toggle">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => toggleDay(d.key)}
                    />
                    {d.label}
                  </label>
                  {enabled ? (
                    <div className="crm-day-times">
                      <input
                        type="time"
                        value={w.start}
                        onChange={(e) => setWindow(d.key, 'start', e.target.value)}
                        aria-label={`${d.label} start`}
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={w.end}
                        onChange={(e) => setWindow(d.key, 'end', e.target.value)}
                        aria-label={`${d.label} end`}
                      />
                    </div>
                  ) : (
                    <span className="crm-modal-hint">Unavailable</span>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <label className="crm-checkbox-label">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          />
          Booking page active
        </label>

        {!compact && (
          <LoadingButton
            type="button"
            className="btn btn-primary"
            loading={saveMutation.isLoading}
            onClick={() => saveMutation.mutate()}
          >
            <FiSave /> Save booking page
          </LoadingButton>
        )}

        {compact && (
          <LoadingButton
            type="button"
            className="btn btn-primary"
            loading={saveMutation.isLoading}
            onClick={() => saveMutation.mutate()}
          >
            <FiSave /> Save &amp; publish
          </LoadingButton>
        )}
      </div>
    </div>
  );
}
